import React from 'react';
import { ArrowRight } from 'lucide-react';

/**
 * TopPriorityRail — the dark right rail on the Mission Control
 * Overview. Surfaces the single active value constraint (or a calm
 * "holding steady" state). The whole block is vertically centred so it
 * fills its row height as one balanced group beside the taller chain
 * card (no stretched empty band).
 */
export default function TopPriorityRail({
  hasLeak, stageLabel, leakPct, rootCauseLabel, recoveryPct, onPrimary, primaryLabel, streak = 0,
}) {
  return (
    <div
      className="relative w-full overflow-hidden rounded-2xl bg-[#111827] ring-1 ring-[#1f2a44] p-6 shadow-xl flex flex-col justify-center"
      data-testid="home-top-priority-rail"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#44b6d2] to-transparent" aria-hidden="true" />
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#44b6d2]">Top Priority</p>
      {hasLeak ? (
        <>
          <h3 className="text-xl font-bold text-slate-100 leading-snug mt-2" data-testid="top-priority-headline">
            {stageLabel} is constraining value transfer
          </h3>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-4xl font-extrabold text-rose-400 tabular-nums">-{leakPct} pts</span>
            <span className="text-xs text-slate-400">value lost at this stage</span>
          </div>
          <p className="mt-2 text-[11px] text-slate-500" data-testid="top-priority-streak">
            {streak < 1
              ? 'New constraint this period'
              : `Bottleneck for ~${streak} month${streak === 1 ? '' : 's'} running`}
          </p>
          <div className="mt-4 space-y-3 text-sm">
            {rootCauseLabel && (
              <div className="flex items-center justify-between gap-3 border-t border-[#1f2a44] pt-3">
                <span className="text-[11px] uppercase tracking-wider text-slate-500">Root cause</span>
                <span className="font-semibold text-slate-100 text-right">{rootCauseLabel}</span>
              </div>
            )}
            <div className="flex items-center justify-between gap-3 border-t border-[#1f2a44] pt-3">
              <span className="text-[11px] uppercase tracking-wider text-slate-500">Potential recovery</span>
              <span className="font-extrabold text-emerald-400 tabular-nums">+{recoveryPct}%</span>
            </div>
          </div>
        </>
      ) : (
        <>
          <h3 className="text-xl font-bold text-slate-100 leading-snug mt-2" data-testid="top-priority-headline">
            Value transfer is holding steady
          </h3>
          <p className="text-sm text-slate-400 mt-2">No stage inside the Missing Middle is dropping value right now. Keep proving impact and widening data coverage to protect the trend.</p>
        </>
      )}
      <button
        type="button"
        onClick={onPrimary}
        className="mt-5 group/tp inline-flex self-start text-sm"
        data-testid="top-priority-action-btn"
      >
        <span className="inline-flex items-center gap-2 rounded-full bg-[#44b6d2] px-4 py-2 font-bold text-[#04222b] transition-transform group-hover/tp:translate-x-0.5">
          {primaryLabel} <ArrowRight className="h-4 w-4" />
        </span>
      </button>
    </div>
  );
}
